import { ExternalLink, Plus, RefreshCw, RotateCcw, Sparkles, Trash2, Wrench } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import type { KbView } from "@/lib/types";
import { cn } from "@/lib/utils";

import { CreatePageDialog } from "./create-page-dialog";
import { PageRenderer } from "./page-renderer";
import { openWorkshopFullPage } from "./standalone-html";
import {
  createWorkshopPageRemote,
  deleteWorkshopPageRemote,
  fetchWorkshopPages,
  reanalyzeWorkshopPageRemote,
  seedIntroPageRemote,
  updateWorkshopPageRemote,
  workshopLoadErrorMessage,
} from "./store";
import type { Widget, WorkshopPage } from "./types";

interface WorkshopBrowserProps {
  teamId: string;
  kbs: KbView[];
}

/**
 * Workshop — server-persisted pages under ``GET/POST /v1/workshop/pages``
 * (team scoped via session). 「重新分析」 replays the saved ``source_prompt``.
 */
export function WorkshopBrowser({ teamId: _teamId, kbs }: WorkshopBrowserProps) {
  const [pages, setPages] = useState<WorkshopPage[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reanalyzeBusy, setReanalyzeBusy] = useState(false);

  const defaultKbId = useMemo(() => {
    const personal = kbs.find((kb) => kb.scope === "personal");
    return personal?.id ?? kbs[0]?.id ?? null;
  }, [kbs]);
  const defaultKbName = useMemo(() => {
    const found = kbs.find((kb) => kb.id === defaultKbId);
    return found?.name ?? null;
  }, [kbs, defaultKbId]);

  const reload = useCallback(async (): Promise<WorkshopPage[]> => {
    let list = await fetchWorkshopPages();
    if (list.length === 0) {
      const seeded = await seedIntroPageRemote({ id: defaultKbId, name: defaultKbName });
      list = [seeded];
    }
    return list;
  }, [defaultKbId, defaultKbName]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    (async () => {
      try {
        const list = await reload();
        if (!cancelled) {
          setPages(list);
          setActiveId((prev) => {
            if (prev && list.some((p) => p.id === prev)) return prev;
            return list[0]?.id ?? null;
          });
        }
      } catch (e) {
        const msg = workshopLoadErrorMessage(e);
        if (!cancelled) {
          setLoadError(msg);
          toast.error(msg);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reload]);

  const activePage = pages.find((p) => p.id === activeId) ?? null;

  const handleCreate = useCallback(
    async (draft: Omit<WorkshopPage, "id" | "created_at" | "updated_at">) => {
      try {
        const created = await createWorkshopPageRemote(draft);
        setPages((prev) => [created, ...prev]);
        setActiveId(created.id);
        toast.success(`已创建「${created.title}」`);
      } catch (e) {
        toast.error(workshopLoadErrorMessage(e));
      }
    },
    [],
  );

  const handleDelete = useCallback(
    async (id: string) => {
      const page = pages.find((p) => p.id === id);
      if (!page) return;
      if (!window.confirm(`删除「${page.title}」？删除后不可恢复。`)) return;
      try {
        await deleteWorkshopPageRemote(id);
        setPages((prev) => {
          const next = prev.filter((p) => p.id !== id);
          if (id === activeId) {
            setActiveId(next[0]?.id ?? null);
          }
          return next;
        });
        toast.success("已删除");
      } catch (e) {
        toast.error(workshopLoadErrorMessage(e));
      }
    },
    [pages, activeId],
  );

  const handleResetSeeds = useCallback(async () => {
    if (!window.confirm("将删除当前团队下全部 Workshop 页面并只保留入门示例，确定继续？")) return;
    try {
      const all = await fetchWorkshopPages();
      await Promise.all(all.map((p) => deleteWorkshopPageRemote(p.id)));
      const seeded = await seedIntroPageRemote({ id: defaultKbId, name: defaultKbName });
      const next = [seeded];
      setPages(next);
      setActiveId(seeded.id);
      toast.success("已重置为示例");
    } catch (e) {
      toast.error(workshopLoadErrorMessage(e));
    }
  }, [defaultKbId, defaultKbName]);

  const handleWidgetChange = useCallback(
    async (slotId: string, next: Widget): Promise<void> => {
      if (!activePage) return;
      const updatedWidgets = activePage.widgets.map((s) =>
        s.id === slotId ? { ...s, widget: next } : s,
      );
      try {
        const persisted = await updateWorkshopPageRemote(activePage.id, {
          widgets: updatedWidgets,
        });
        setPages((prev) => prev.map((p) => (p.id === persisted.id ? persisted : p)));
      } catch (e) {
        toast.error(workshopLoadErrorMessage(e));
      }
    },
    [activePage],
  );

  const handleTitleCommit = useCallback(
    async (title: string, description: string): Promise<void> => {
      if (!activePage) return;
      try {
        const next = await updateWorkshopPageRemote(activePage.id, {
          title: title.trim() || activePage.title,
          description,
        });
        setPages((prev) => prev.map((p) => (p.id === next.id ? next : p)));
        setEditingTitle(false);
      } catch (e) {
        toast.error(workshopLoadErrorMessage(e));
      }
    },
    [activePage],
  );

  const handleReanalyze = useCallback(async () => {
    if (!activePage) return;
    setReanalyzeBusy(true);
    try {
      const next = await reanalyzeWorkshopPageRemote(activePage.id);
      setPages((prev) => prev.map((p) => (p.id === next.id ? next : p)));
      toast.success(
        next.last_analyze_source === "llm"
          ? "已结合知识库与模型重新生成"
          : "已根据最新 KB 数据重新汇总",
      );
    } catch (e) {
      toast.error(workshopLoadErrorMessage(e));
    } finally {
      setReanalyzeBusy(false);
    }
  }, [activePage]);

  const canReanalyze = Boolean(
    activePage?.kb_id && activePage.source_prompt && activePage.source_prompt.trim(),
  );

  if (loading) {
    return (
      <div className="rounded-2xl border border-dashed border-border bg-muted/20 p-12 text-center text-[13px] text-muted-foreground">
        正在从服务器加载 Workshop 页面…
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="space-y-3 rounded-2xl border border-destructive/30 bg-destructive/5 p-6 text-[13px]">
        <div className="font-semibold text-destructive">无法加载 Workshop</div>
        <div className="text-muted-foreground">{loadError}</div>
        <details className="rounded-md border border-border bg-card p-3 text-[12px] leading-relaxed">
          <summary className="cursor-pointer font-medium">如何修复</summary>
          <div className="mt-2 space-y-1.5 text-muted-foreground">
            <p>Workshop 现在使用 Postgres 持久化，需要应用最新迁移：</p>
            <pre className="overflow-x-auto rounded bg-muted/40 p-2 text-[11.5px]">
              cd backend{"\n"}uv run alembic -c packages/kos-db/alembic.ini upgrade head
            </pre>
            <p>
              或运行 <code>tools/init-db.sh</code>。完成后刷新本页。
            </p>
          </div>
        </details>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => {
            setLoadError(null);
            setLoading(true);
            reload()
              .then((list) => {
                setPages(list);
                setActiveId(list[0]?.id ?? null);
              })
              .catch((e) => setLoadError(workshopLoadErrorMessage(e)))
              .finally(() => setLoading(false));
          }}
        >
          <RotateCcw className="mr-1 h-3.5 w-3.5" />
          重试
        </Button>
      </div>
    );
  }

  return (
    <div className="grid gap-4 lg:grid-cols-[260px_1fr]">
      <aside className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
            页面 ({pages.length})
          </div>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => setCreateOpen(true)}
            className="h-7 px-2 text-[11px]"
          >
            <Plus className="mr-1 h-3 w-3" />
            新建
          </Button>
        </div>

        {pages.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border bg-muted/30 p-4 text-center">
            <Wrench className="mx-auto h-5 w-5 text-muted-foreground" />
            <p className="mt-2 text-[12px] text-muted-foreground">
              还没有页面。点「新建」或加载示例：
            </p>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => void handleResetSeeds()}
              className="mt-2 text-[11px]"
            >
              <Sparkles className="mr-1 h-3 w-3" />
              加载示例
            </Button>
          </div>
        ) : (
          <ul className="space-y-1">
            {pages.map((page) => (
              <li key={page.id}>
                <button
                  type="button"
                  onClick={() => setActiveId(page.id)}
                  className={cn(
                    "group flex w-full items-start justify-between gap-2 rounded-lg border p-2.5 text-left transition-colors",
                    page.id === activeId
                      ? "border-primary/50 bg-primary/5"
                      : "border-border hover:bg-muted",
                  )}
                >
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[12.5px] font-medium leading-tight">
                      {page.title}
                    </div>
                    <div className="mt-1 flex items-center gap-1.5">
                      {page.kb_name ? (
                        <Badge variant="outline" className="text-[10px]">
                          {page.kb_name}
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-[10px]">
                          all KBs
                        </Badge>
                      )}
                      <span className="text-[10px] text-muted-foreground">
                        {new Date(page.updated_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}

        {pages.length > 0 ? (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => void handleResetSeeds()}
            className="w-full justify-start text-[11px] text-muted-foreground hover:text-foreground"
          >
            <RotateCcw className="mr-1 h-3 w-3" />
            重置为示例
          </Button>
        ) : null}
      </aside>

      <main className="min-w-0 space-y-4">
        {activePage ? (
          <>
            <PageTitleBar
              key={activePage.id}
              page={activePage}
              editing={editingTitle}
              onEdit={() => setEditingTitle(true)}
              onCommit={(t, d) => void handleTitleCommit(t, d)}
              onCancel={() => setEditingTitle(false)}
              onDelete={() => void handleDelete(activePage.id)}
              onOpenFullPage={() => {
                const ok = openWorkshopFullPage(activePage);
                if (!ok) toast.error("无法打开新标签页，请检查浏览器弹窗拦截");
              }}
              onReanalyze={() => void handleReanalyze()}
              reanalyzeBusy={reanalyzeBusy}
              canReanalyze={canReanalyze}
            />
            <PageRenderer page={activePage} onWidgetChange={handleWidgetChange} />
          </>
        ) : (
          <div className="rounded-2xl border border-dashed border-border bg-muted/20 p-12 text-center">
            <Wrench className="mx-auto h-8 w-8 text-muted-foreground" />
            <h3 className="mt-3 text-sm font-medium">没有选中的页面</h3>
            <p className="mt-1 text-[12px] text-muted-foreground">
              在左侧创建一个页面来开始分析你的知识库。
            </p>
            <Button type="button" onClick={() => setCreateOpen(true)} className="mt-4">
              <Plus className="mr-1.5 h-3.5 w-3.5" />
              新建页面
            </Button>
          </div>
        )}
      </main>

      <CreatePageDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        kbs={kbs}
        defaultKbId={defaultKbId}
        onCreate={(d) => void handleCreate(d)}
      />
    </div>
  );
}

interface PageTitleBarProps {
  page: WorkshopPage;
  editing: boolean;
  onEdit: () => void;
  onCommit: (title: string, description: string) => void;
  onCancel: () => void;
  onDelete: () => void;
  onOpenFullPage: () => void;
  onReanalyze: () => void;
  reanalyzeBusy: boolean;
  canReanalyze: boolean;
}

function PageTitleBar({
  page,
  editing,
  onEdit,
  onCommit,
  onCancel,
  onDelete,
  onOpenFullPage,
  onReanalyze,
  reanalyzeBusy,
  canReanalyze,
}: PageTitleBarProps) {
  const [draftTitle, setDraftTitle] = useState(page.title);
  const [draftDesc, setDraftDesc] = useState(page.description ?? "");

  if (editing) {
    return (
      <div className="space-y-2 rounded-2xl border border-border bg-card p-4">
        <Input
          value={draftTitle}
          onChange={(e) => setDraftTitle(e.target.value)}
          className="text-base font-semibold"
          autoFocus
        />
        <Textarea
          value={draftDesc}
          onChange={(e) => setDraftDesc(e.target.value)}
          rows={2}
          placeholder="加点说明（可选）"
          className="text-[12px]"
        />
        <div className="flex justify-end gap-1.5">
          <Button type="button" size="sm" variant="ghost" onClick={onCancel}>
            取消
          </Button>
          <Button type="button" size="sm" onClick={() => onCommit(draftTitle, draftDesc)}>
            保存
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="truncate text-base font-semibold tracking-tight">{page.title}</h2>
          {page.kb_name ? (
            <Badge variant="secondary" className="text-[10px]">
              KB · {page.kb_name}
            </Badge>
          ) : null}
          {page.last_analyze_source ? (
            <Badge variant="outline" className="text-[10px]">
              {page.last_analyze_source === "llm" ? "LLM" : "自动汇总"}
            </Badge>
          ) : null}
        </div>
        {page.description ? (
          <p className="text-[12px] leading-relaxed text-muted-foreground">{page.description}</p>
        ) : null}
        <div className="text-[10.5px] uppercase tracking-[0.08em] text-muted-foreground/80">
          updated {new Date(page.updated_at).toLocaleString()}
        </div>
      </div>
      <div className="flex shrink-0 flex-wrap justify-end gap-1.5">
        <Button
          type="button"
          size="sm"
          variant="default"
          disabled={!canReanalyze || reanalyzeBusy}
          onClick={onReanalyze}
          title={
            canReanalyze
              ? "使用保存的需求描述重新拉取 KB 并生成"
              : "仅「描述需求」创建的页面可重跑（需已绑定知识库）"
          }
        >
          <RefreshCw className={cn("mr-1 h-3.5 w-3.5", reanalyzeBusy && "animate-spin")} />
          {reanalyzeBusy ? "生成中…" : "重新分析"}
        </Button>
        <Button type="button" size="sm" variant="secondary" onClick={onOpenFullPage}>
          <ExternalLink className="mr-1 h-3.5 w-3.5" />
          完整页面
        </Button>
        <Button type="button" size="sm" variant="outline" onClick={onEdit}>
          编辑标题
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={onDelete}
          className="text-destructive hover:text-destructive"
        >
          <Trash2 className="mr-1 h-3.5 w-3.5" />
          删除
        </Button>
      </div>
    </div>
  );
}
